import React from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Droplets,
  Info,
  Leaf,
  Package,
  Recycle,
  Sparkles,
  Truck,
  X,
  UtensilsCrossed,
} from 'lucide-react';
import {
  NUTRITION_FIELDS,
  normalizeBodycareDetails,
  normalizeFoodDetails,
  normalizeHouseholdDetails,
  normalizeNutrition,
} from '../data/products';

const formatNutritionValue = (value, unit) => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) return '';
  if (/[a-z%]$/i.test(normalizedValue)) return normalizedValue;
  return `${normalizedValue} ${unit}`;
};

const colorWithAlpha = (color, alpha) => {
  const match = /^#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})$/i.exec(String(color || ''));
  if (!match) return undefined;
  const [, red, green, blue] = match;
  return `rgba(${Number.parseInt(red, 16)}, ${Number.parseInt(green, 16)}, ${Number.parseInt(blue, 16)}, ${alpha})`;
};

const getReadableTextColor = (backgroundColor) => {
  const match = /^#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})$/i.exec(
    String(backgroundColor || ''),
  );
  if (!match) return '#FFFFFF';

  const toLinear = (hexValue) => {
    const channel = Number.parseInt(hexValue, 16) / 255;
    return channel <= 0.04045
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  };
  const [, red, green, blue] = match;
  const backgroundLuminance = (
    0.2126 * toLinear(red)
    + 0.7152 * toLinear(green)
    + 0.0722 * toLinear(blue)
  );
  const darkTextLuminance = (
    0.2126 * toLinear('0B')
    + 0.7152 * toLinear('2F')
    + 0.0722 * toLinear('22')
  );
  const lightContrast = 1.05 / (backgroundLuminance + 0.05);
  const darkContrast = (backgroundLuminance + 0.05) / (darkTextLuminance + 0.05);
  return lightContrast >= darkContrast ? '#FFFFFF' : '#0B2F22';
};

const buildNutritionRows = (nutrition) => {
  const energyValue = [
    formatNutritionValue(nutrition.energyKj, 'kJ'),
    formatNutritionValue(nutrition.energyKcal, 'kcal'),
  ]
    .filter(Boolean)
    .join(' / ');
  const rows = energyValue
    ? [{ key: 'energy', label: 'Energia', value: energyValue, indented: false }]
    : [];

  NUTRITION_FIELDS.filter(({ key }) => key !== 'energyKj' && key !== 'energyKcal').forEach(
    (field) => {
      const value = formatNutritionValue(nutrition[field.key], field.unit);
      if (value) rows.push({ ...field, value });
    },
  );
  return rows;
};

function DetailPanel({ icon: Icon, title, children, className = '' }) {
  return (
    <section className={`rounded-3xl border border-border/70 bg-card p-6 shadow-sm ${className}`}>
      <div className="mb-4 flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-secondary text-primary">
          <Icon className="h-5 w-5" strokeWidth={1.8} />
        </span>
        <h3 className="font-display text-lg font-semibold">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function PillList({ items, tone = 'green' }) {
  if (!items?.length) return null;
  const toneClass = tone === 'warm'
    ? 'border-[#D9BE8B]/60 bg-[#FBF4E7] text-[#6F5526]'
    : 'border-primary/15 bg-primary/[0.055] text-primary';
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <span
          key={item}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${toneClass}`}
        >
          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
          {item}
        </span>
      ))}
    </div>
  );
}

function LabelValue({ label, value }) {
  if (!value) return null;
  return (
    <div className="border-b border-border/60 py-3 last:border-b-0">
      <dt className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">{label}</dt>
      <dd className="mt-1.5 text-sm leading-relaxed text-foreground/85">{value}</dd>
    </div>
  );
}

function IntroPanel({ description }) {
  if (!description) return null;
  return (
    <DetailPanel icon={Info} title="A termékről">
      <p className="whitespace-pre-line text-[15px] leading-7 text-foreground/78">{description}</p>
    </DetailPanel>
  );
}

function FoodSectionHeading({ eyebrow, title, description, accentColor }) {
  return (
    <div className="mb-8 max-w-2xl">
      {eyebrow ? (
        <p
          className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8A6A22]"
          style={accentColor ? { color: accentColor } : undefined}
        >
          {eyebrow}
        </p>
      ) : null}
      <h2 className="font-display text-2xl font-semibold leading-tight text-primary md:text-3xl">
        {title}
      </h2>
      {description ? (
        <p className="mt-3 max-w-xl whitespace-pre-line text-sm leading-6 text-muted-foreground">
          {description}
        </p>
      ) : null}
    </div>
  );
}

function FoodDetails({ product, onAddToCart }) {
  const details = normalizeFoodDetails(product.foodDetails);
  const compositionColor = details.compositionColor;
  const compositionTextColor = getReadableTextColor(compositionColor);
  const nutritionColor = details.nutritionColor;
  const benefitRows = details.didYouKnow
    ? [
        ...details.benefits,
        { title: 'Érdekesség', description: details.didYouKnow },
      ]
    : details.benefits;
  const nutrition = normalizeNutrition(product.nutrition);
  const nutritionRows = buildNutritionRows(nutrition);
  const macroFields = [
    { key: 'protein', label: 'Fehérje', unit: 'g' },
    { key: 'fat', label: 'Zsír', unit: 'g' },
    { key: 'carbohydrate', label: 'Szénhidrát', unit: 'g' },
    { key: 'fiber', label: 'Rost', unit: 'g' },
  ].filter(({ key }) => String(nutrition[key] || '').trim());
  const macroMax = Math.max(
    ...macroFields.map(({ key }) => Number.parseFloat(String(nutrition[key]).replace(',', '.')) || 0),
    1,
  );
  const recipes = details.recipeIdeas.length > 0
    ? details.recipeIdeas
    : details.usageIdeas.slice(0, 3).map((title) => ({ title, image: '' }));
  const hasComposition = details.ingredientHighlight
    || details.ingredientHighlightLabel
    || details.excludedIngredients.length > 0;
  const hasUsageCopy = details.servingSuggestion || details.preparation;
  const energyValue = [
    formatNutritionValue(nutrition.energyKj, 'kJ'),
    formatNutritionValue(nutrition.energyKcal, 'kcal'),
  ].filter(Boolean).join(' / ');
  const productRows = [
    { label: 'Összetevők', value: (product.ingredients || []).join(', ') },
    { label: 'Kiszerelés', value: details.netQuantity },
    { label: 'Energia', value: energyValue ? `${energyValue} / ${nutrition.basis}` : '' },
    { label: 'Allergének', value: details.allergens.join(', ') },
    { label: 'Származási hely', value: details.origin },
    { label: 'Tárolás', value: product.storage },
  ].filter(({ value }) => value);

  return (
    <div className="-mx-6 overflow-hidden border-y border-primary/10 bg-background text-foreground sm:rounded-[2.5rem] sm:border md:mx-0">
      {hasComposition ? (
        <section
          className="border-b border-primary/10 px-6 py-14 md:px-10 md:py-20 lg:px-16"
          style={{ backgroundColor: colorWithAlpha(compositionColor, 0.045) }}
        >
          <div className="grid items-center gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <div className="mx-auto text-center">
              <div
                className="flex h-56 w-56 items-center justify-center rounded-full border-[28px] bg-card shadow-[0_18px_50px_rgba(15,61,44,0.12)] md:h-64 md:w-64 md:border-[34px]"
                style={{ borderColor: compositionColor }}
              >
                <div>
                  <p className="font-display text-4xl font-semibold" style={{ color: compositionColor }}>
                    {details.ingredientHighlight || product.ingredients?.length || '–'}
                  </p>
                  <p className="mt-1 max-w-28 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    {details.ingredientHighlightLabel || 'összetevő'}
                  </p>
                </div>
              </div>
              {product.ingredients?.length ? (
                <p className="mx-auto mt-5 max-w-xs text-xs leading-5 text-muted-foreground">
                  Összetevők aránya – átlátható, őszinte termékadatok
                </p>
              ) : null}
            </div>
            <div>
              <FoodSectionHeading
                eyebrow="Tiszta összetétel"
                accentColor={compositionColor}
                title="Ami nincs benne, az is számít"
                description={details.ingredientHighlightLabel
                  ? `${details.ingredientHighlightLabel} – felesleges adalékok nélkül.`
                  : 'Átlátható összetétel, tudatosan elhagyott adalékokkal.'}
              />
              {details.excludedIngredients.length > 0 ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {details.excludedIngredients.map((item) => (
                    <div key={item} className="flex items-center gap-3 rounded-full border border-primary/10 bg-card px-4 py-3 shadow-sm">
                      <span
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white"
                        style={{ backgroundColor: compositionColor }}
                      >
                        <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                      </span>
                      <span className="text-sm text-foreground/75 line-through" style={{ textDecorationColor: colorWithAlpha(compositionColor, 0.7) }}>{item}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {nutritionRows.length > 0 ? (
        <section
          className="border-b border-primary/10 px-6 py-14 md:px-10 md:py-20 lg:px-16"
          style={{ backgroundColor: colorWithAlpha(nutritionColor, 0.13) }}
        >
          <FoodSectionHeading
            eyebrow="Átlátható értékek"
            accentColor={nutritionColor}
            title={details.nutritionIntro || 'Tápérték, ránézésre'}
            description={details.nutritionIntro ? undefined : `Átlagos tápérték ${nutrition.basis} termékben.`}
          />
          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            {macroFields.length > 0 ? (
              <div className="rounded-3xl border border-primary/10 bg-card p-6 text-foreground shadow-[0_18px_50px_rgba(15,61,44,0.1)] sm:p-8">
                <div className="space-y-7">
                  {macroFields.map(({ key, label, unit }) => {
                    const numericValue = Number.parseFloat(String(nutrition[key]).replace(',', '.')) || 0;
                    const width = Math.max(8, Math.round((numericValue / macroMax) * 100));
                    return (
                      <div key={key}>
                        <div className="mb-2 flex items-end justify-between gap-4">
                          <span className="text-sm font-semibold">{label}</span>
                          <span className="font-display text-xl font-semibold" style={{ color: nutritionColor }}>
                            {formatNutritionValue(nutrition[key], unit)}
                          </span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-[#E6DDC8]">
                          <div className="h-full rounded-full" style={{ width: `${width}%`, backgroundColor: nutritionColor }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <div className="overflow-hidden rounded-3xl border border-primary/10 bg-card shadow-sm">
              <table className="w-full border-collapse text-sm">
                <caption className="sr-only">Átlagos tápérték {nutrition.basis} termékben</caption>
                <thead className="bg-primary/[0.06]">
                  <tr>
                    <th scope="col" className="px-5 py-4 text-left font-semibold text-primary">Tápanyag</th>
                    <th scope="col" className="px-5 py-4 text-right font-semibold" style={{ color: nutritionColor }}>{nutrition.basis}</th>
                  </tr>
                </thead>
                <tbody>
                  {nutritionRows.map((row) => (
                    <tr key={row.key} className="border-t border-primary/10">
                      <th scope="row" className={`px-5 py-3 text-left font-normal ${row.indented ? 'pl-8 text-muted-foreground' : 'text-foreground/80'}`}>
                        {row.label}
                      </th>
                      <td className="whitespace-nowrap px-5 py-3 text-right font-medium text-primary">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {(hasUsageCopy || recipes.length > 0) ? (
        <section className="border-b border-primary/10 px-6 py-14 md:px-10 md:py-20 lg:px-16">
          <FoodSectionHeading
            eyebrow="Ötletek a mindennapokra"
            title="Készíts belőle valami finomat"
            description={details.servingSuggestion || details.preparation}
          />
          {hasUsageCopy && details.servingSuggestion && details.preparation ? (
            <div className="mb-7 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-primary/10 bg-card p-5 shadow-sm">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A6A22]">Tálalási javaslat</p>
                <p className="mt-2 text-sm leading-6 text-foreground/75">{details.servingSuggestion}</p>
              </div>
              <div className="rounded-2xl border border-primary/10 bg-card p-5 shadow-sm">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A6A22]">Adagolás</p>
                <p className="mt-2 text-sm leading-6 text-foreground/75">{details.preparation}</p>
              </div>
            </div>
          ) : null}
          {recipes.length > 0 ? (
            <div className="grid gap-5 md:grid-cols-3">
              {recipes.map((recipe, index) => (
                <article key={`${recipe.title}-${index}`} className="group overflow-hidden rounded-3xl border border-primary/10 bg-card text-foreground shadow-sm">
                  <div className="aspect-[4/3] overflow-hidden bg-[#E7EDDF]">
                    {recipe.image ? (
                      <img src={recipe.image} alt={recipe.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.035]" />
                    ) : (
                      <div className="flex h-full items-center justify-center">
                        <UtensilsCrossed className="h-9 w-9 text-[#7F9988]" strokeWidth={1.4} />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-4 px-5 py-4">
                    <h3 className="font-display text-base font-semibold">{recipe.title}</h3>
                    <ArrowRight className="h-4 w-4 shrink-0 text-[#B28D2F] transition-transform group-hover:translate-x-1" />
                  </div>
                </article>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {benefitRows.length > 0 ? (
        <section className="border-b border-primary/10 bg-secondary/55 px-6 py-14 md:px-10 md:py-20 lg:px-16">
          <FoodSectionHeading eyebrow="Több, mint finom" title="Azon túl, hogy finom" />
          <div className="space-y-4">
            {benefitRows.map((benefit, index) => (
              <article
                key={`${benefit.title}-${index}`}
                className="grid overflow-hidden rounded-3xl border border-primary/10 bg-card shadow-sm md:grid-cols-[0.3fr_0.7fr]"
              >
                <div className="flex items-center p-6 sm:p-8">
                  <h3 className="font-display text-lg font-semibold text-primary">{benefit.title}</h3>
                </div>
                <div
                  className="p-6 sm:p-8"
                  style={{ backgroundColor: compositionColor, color: compositionTextColor }}
                >
                  <p
                    className="text-[10px] font-bold uppercase tracking-[0.22em]"
                    style={{ color: compositionTextColor }}
                  >
                    Tudtad?
                  </p>
                  {benefit.description ? (
                    <p
                      className="mt-3 whitespace-pre-line text-sm leading-6 sm:text-base"
                      style={{ color: colorWithAlpha(compositionTextColor, 0.9) }}
                    >
                      {benefit.description}
                    </p>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {productRows.length > 0 ? (
        <section className="border-b border-primary/10 bg-card/35 px-6 py-14 md:px-10 md:py-20 lg:px-16">
          <div className="mx-auto max-w-3xl">
            <FoodSectionHeading eyebrow="Minden egy helyen" title="Termékadatok" />
            <dl>
              {productRows.map((row) => (
                <div key={row.label} className="grid gap-2 border-b border-primary/10 py-4 sm:grid-cols-[0.36fr_0.64fr] sm:gap-6">
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{row.label}</dt>
                  <dd className="text-sm leading-6 text-foreground/80 sm:text-right">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      ) : null}

      <section
        className="px-6 py-14 text-center md:px-10 md:py-16 lg:px-16"
        style={{ backgroundColor: compositionColor, color: compositionTextColor }}
      >
        <h2
          className="font-display text-2xl font-semibold md:text-3xl"
          style={{ color: compositionTextColor }}
        >
          {details.ctaTitle || 'Tiszta összetevők. Tudatos választás.'}
        </h2>
        <p
          className="mx-auto mt-3 max-w-2xl text-sm leading-6"
          style={{ color: colorWithAlpha(compositionTextColor, 0.74) }}
        >
          {details.ctaText || product.shortDescription}
        </p>
        {onAddToCart ? (
          <button
            type="button"
            onClick={onAddToCart}
            className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-[#D7B55B] px-7 py-3 text-xs font-semibold uppercase tracking-[0.1em] text-[#0B2F22] transition-transform hover:-translate-y-0.5"
          >
            Kosárba – {product.price.toLocaleString('hu-HU')} Ft
            <ArrowRight className="h-4 w-4" />
          </button>
        ) : null}
      </section>
    </div>
  );
}

function MarketingDetails({ details, product, onAddToCart }) {
  const hasBenefits = details.benefits.length > 0;
  const hasCta = details.ctaTitle || details.ctaText;
  if (!hasBenefits && !hasCta) return null;

  return (
    <section className="mt-10 overflow-hidden rounded-[2rem] border border-primary/10 bg-secondary/55">
      {hasBenefits ? (
        <div className="px-6 py-10 md:px-10 md:py-12">
          <FoodSectionHeading eyebrow="Előnyök és érdekességek" title="Amiért érdemes választani" />
          <div className="space-y-4">
            {details.benefits.map((benefit, index) => (
              <article
                key={`${benefit.title}-${index}`}
                className="grid overflow-hidden rounded-3xl border border-primary/10 bg-card shadow-sm md:grid-cols-[0.3fr_0.7fr]"
              >
                <div className="flex items-center p-6 sm:p-8">
                  <h3 className="font-display text-lg font-semibold text-primary">{benefit.title}</h3>
                </div>
                <div className="bg-[#0F3D2C] p-6 text-white sm:p-8">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white">Tudtad?</p>
                  {benefit.description ? (
                    <p className="mt-3 whitespace-pre-line text-sm leading-6 text-white/90 sm:text-base">
                      {benefit.description}
                    </p>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </div>
      ) : null}
      {hasCta ? (
        <div className="bg-primary px-6 py-12 text-center text-primary-foreground md:px-10">
          {details.ctaTitle ? (
            <h2 className="font-display text-2xl font-semibold text-primary-foreground md:text-3xl">
              {details.ctaTitle}
            </h2>
          ) : null}
          {details.ctaText ? (
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-primary-foreground/70">
              {details.ctaText}
            </p>
          ) : null}
          {onAddToCart ? (
            <button
              type="button"
              onClick={onAddToCart}
              className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-[#D7B55B] px-7 py-3 text-xs font-semibold uppercase tracking-[0.1em] text-[#0B2F22] transition-transform hover:-translate-y-0.5"
            >
              Kosárba – {product.price.toLocaleString('hu-HU')} Ft
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

const clampImpactPercentage = (value) => Math.min(
  100,
  Math.max(0, Number.parseFloat(String(value).replace(',', '.')) || 0),
);

function WaterImpactSection({ details }) {
  const accentColor = details.waterImpactColor;
  const accentTextColor = getReadableTextColor(accentColor);
  const averageWaterPercentage = clampImpactPercentage(details.averageWaterPercentage);
  const productWaterPercentage = clampImpactPercentage(details.productWaterPercentage);
  const hasComparison = details.averageWaterPercentage
    || details.productWaterPercentage
    || details.waterComparisonText;
  const hasTransport = details.transportCostHighlight || details.transportCostText;

  return (
    <section className="border-b border-[#DECFAF]/70 px-6 py-14 md:px-10 md:py-16 lg:px-14">
      <FoodSectionHeading
        eyebrow="Víz és szállítás"
        title={details.waterImpactTitle || 'Mennyivel kevesebb vizet szállítasz?'}
        description={details.waterImpactIntro}
        accentColor={accentColor}
      />
      {(hasComparison || hasTransport) ? (
        <div className="grid gap-6 lg:grid-cols-[1.08fr_0.92fr]">
          {hasComparison ? (
            <div
              className="rounded-3xl border bg-card p-6 shadow-sm sm:p-8"
              style={{ borderColor: colorWithAlpha(accentColor, 0.34) }}
            >
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex h-10 w-10 items-center justify-center rounded-2xl"
                  style={{ backgroundColor: colorWithAlpha(accentColor, 0.14), color: accentColor }}
                >
                  <Droplets className="h-5 w-5" />
                </span>
                <h3 className="font-display text-lg font-semibold text-primary">Víztartalom összehasonlítása</h3>
              </div>
              <div className="mt-8 grid grid-cols-2 gap-6 sm:gap-10">
                {[
                  { label: 'Átlagos termék', value: averageWaterPercentage, raw: details.averageWaterPercentage },
                  { label: 'Ez a termék', value: productWaterPercentage, raw: details.productWaterPercentage },
                ].map((item) => (
                  <div key={item.label} className="text-center">
                    <div
                      className="relative mx-auto h-48 w-24 overflow-hidden rounded-b-[2rem] rounded-t-xl border-2"
                      style={{
                        backgroundColor: colorWithAlpha(accentColor, 0.07),
                        borderColor: colorWithAlpha(accentColor, 0.5),
                      }}
                    >
                      <div
                        className="absolute inset-x-0 bottom-0 transition-[height] duration-700"
                        style={{ height: `${item.value}%`, backgroundColor: accentColor }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span
                          className="relative z-10 rounded-full bg-white/90 px-3 py-1 font-display text-xl font-semibold shadow-sm"
                          style={{ color: accentColor }}
                        >
                          {item.raw || '0'}%
                        </span>
                      </div>
                    </div>
                    <p className="mt-3 text-sm font-semibold text-foreground/80">{item.label}</p>
                  </div>
                ))}
              </div>
              {details.waterComparisonText ? <p className="mt-7 whitespace-pre-line text-sm leading-6 text-foreground/75">{details.waterComparisonText}</p> : null}
            </div>
          ) : null}
          {hasTransport ? (
            <div
              className="flex flex-col justify-center rounded-3xl border p-6 shadow-sm sm:p-8"
              style={{
                backgroundColor: colorWithAlpha(accentColor, 0.075),
                borderColor: colorWithAlpha(accentColor, 0.3),
              }}
            >
              <span
                className="inline-flex h-12 w-12 items-center justify-center rounded-2xl"
                style={{ backgroundColor: accentColor, color: accentTextColor }}
              >
                <Truck className="h-6 w-6" />
              </span>
              <p className="mt-5 text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: accentColor }}>Szállítási hatás</p>
              {details.transportCostHighlight ? <h3 className="mt-2 font-display text-2xl font-semibold leading-tight text-primary">{details.transportCostHighlight}</h3> : null}
              {details.transportCostText ? <p className="mt-4 whitespace-pre-line text-sm leading-6 text-foreground/75">{details.transportCostText}</p> : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {details.waterImpactMetrics.length > 0 ? (
        <div
          className="mt-6 rounded-3xl p-6 shadow-sm sm:p-8"
          style={{ backgroundColor: accentColor, color: accentTextColor }}
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: colorWithAlpha(accentTextColor, 0.72) }}>Amit a szállításból megspórolsz</p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {details.waterImpactMetrics.map((metric, index) => (
              <article
                key={`${metric.label}-${index}`}
                className="rounded-2xl border p-5"
                style={{
                  backgroundColor: colorWithAlpha(accentTextColor, 0.09),
                  borderColor: colorWithAlpha(accentTextColor, 0.16),
                }}
              >
                {metric.value ? <p className="font-display text-2xl font-semibold" style={{ color: accentTextColor }}>{metric.value}</p> : null}
                {metric.label ? <h3 className="mt-1 text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: accentTextColor }}>{metric.label}</h3> : null}
                {metric.note ? <p className="mt-3 text-xs leading-5" style={{ color: colorWithAlpha(accentTextColor, 0.76) }}>{metric.note}</p> : null}
              </article>
            ))}
          </div>
        </div>
      ) : null}
      {details.waterImpactCallout ? (
        <div
          className="mt-6 rounded-3xl border border-dashed p-6 sm:p-8"
          style={{
            backgroundColor: colorWithAlpha(accentColor, 0.07),
            borderColor: colorWithAlpha(accentColor, 0.45),
          }}
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: accentColor }}>Összkép</p>
          <p className="mt-3 whitespace-pre-line font-display text-lg leading-7 text-primary">{details.waterImpactCallout}</p>
        </div>
      ) : null}
    </section>
  );
}

function PlasticImpactSection({ details }) {
  const accentColor = details.plasticImpactColor;
  const plasticPercentage = clampImpactPercentage(details.plasticPercentage);

  return (
    <section className="border-b border-[#DECFAF]/70 px-6 py-14 md:px-10 md:py-16 lg:px-14">
      <FoodSectionHeading
        eyebrow="Kevesebb csomagolási hulladék"
        title={details.plasticImpactTitle || 'Mennyi műanyag eldobását spórolod meg?'}
        description={details.plasticImpactIntro}
        accentColor={accentColor}
      />
      <div className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-[0.92fr_1.08fr]">
          {(details.plasticAmount || details.plasticPercentage) ? (
            <div
              className="rounded-3xl border bg-card p-6 text-center shadow-sm sm:p-8"
              style={{ borderColor: colorWithAlpha(accentColor, 0.32) }}
            >
              <div
                className="mx-auto flex h-48 w-48 items-center justify-center rounded-full p-6"
                style={{ background: `conic-gradient(${accentColor} ${plasticPercentage * 3.6}deg, ${colorWithAlpha(accentColor, 0.14)} 0deg)` }}
              >
                <div className="flex h-full w-full flex-col items-center justify-center rounded-full bg-card px-4">
                  <Recycle className="h-6 w-6" style={{ color: accentColor }} />
                  <p className="mt-2 font-display text-2xl font-semibold text-primary">{details.plasticAmount || `${details.plasticPercentage}%`}</p>
                  {details.plasticAmount && details.plasticPercentage ? <p className="mt-1 text-xs text-muted-foreground">{details.plasticPercentage}% megtakarítás</p> : null}
                </div>
              </div>
              <p className="mt-5 text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: accentColor }}>Műanyag-megtakarítás</p>
            </div>
          ) : null}
          {(details.plasticEquivalent || details.plasticImpactText) ? (
            <div
              className="rounded-3xl border p-6 sm:p-8"
              style={{
                backgroundColor: colorWithAlpha(accentColor, 0.07),
                borderColor: colorWithAlpha(accentColor, 0.3),
              }}
            >
              <span
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl"
                style={{ backgroundColor: colorWithAlpha(accentColor, 0.15), color: accentColor }}
              >
                <AlertTriangle className="h-5 w-5" />
              </span>
              <p className="mt-5 text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: accentColor }}>Mit jelent ez a környezetnek?</p>
              {details.plasticEquivalent ? <h3 className="mt-2 font-display text-2xl font-semibold leading-tight text-primary">{details.plasticEquivalent}</h3> : null}
              {details.plasticImpactText ? <p className="mt-4 whitespace-pre-line text-sm leading-6 text-foreground/75">{details.plasticImpactText}</p> : null}
            </div>
          ) : null}
        </div>
        {details.impactMetrics.length > 0 ? (
          <div className="rounded-3xl border border-[#DECFAF] bg-card p-6 shadow-sm sm:p-8">
            <div className="space-y-7">
              {details.impactMetrics.map((metric, index) => {
                const configuredWidth = Number.parseFloat(metric.barPercentage);
                const barWidth = Number.isFinite(configuredWidth)
                  ? Math.min(100, Math.max(0, configuredWidth))
                  : 50;
                return (
                  <div key={`${metric.label}-${index}`}>
                    <div className="mb-2 flex items-end justify-between gap-4"><p className="text-sm font-semibold text-foreground/85">{metric.label}</p><p className="shrink-0 font-display text-lg font-semibold" style={{ color: accentColor }}>{metric.value}</p></div>
                    <div className="h-2 overflow-hidden rounded-full" style={{ backgroundColor: colorWithAlpha(accentColor, 0.12) }}><div className="h-full rounded-full" style={{ width: `${barWidth}%`, backgroundColor: accentColor }} /></div>
                    {metric.note ? <p className="mt-2 text-[11px] leading-5 text-muted-foreground">{metric.note}</p> : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
      {details.plasticImpactCallout ? (
        <div
          className="mt-6 rounded-3xl border border-dashed p-6 sm:p-8"
          style={{
            backgroundColor: colorWithAlpha(accentColor, 0.07),
            borderColor: colorWithAlpha(accentColor, 0.45),
          }}
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: accentColor }}>Gondolj bele</p>
          <p className="mt-3 whitespace-pre-line font-display text-lg leading-7 text-primary">{details.plasticImpactCallout}</p>
        </div>
      ) : null}
    </section>
  );
}

function HouseholdStoryDetails({ details, product }) {
  const hasWaterImpact = details.waterImpactEnabled;
  const hasPlasticImpact = details.plasticImpactEnabled;
  const hasImpact = hasWaterImpact || hasPlasticImpact;
  const hasComposition = details.compositionTitle
    || details.compositionIntro
    || details.excludedIngredients.length > 0;
  const hasUsageSteps = details.usageSteps.length > 0;
  if (!hasImpact && !hasComposition && !hasUsageSteps) return null;

  const compositionImage = product.galleryImages?.[1]
    || product.galleryImages?.[0]
    || product.image;

  return (
    <div className="mb-10 overflow-hidden rounded-[2.5rem] border border-primary/10 bg-[#FCF8EF] text-foreground">
      {hasWaterImpact ? <WaterImpactSection details={details} /> : null}
      {hasPlasticImpact ? <PlasticImpactSection details={details} /> : null}

      {hasComposition ? (
        <section className="border-b border-[#DECFAF]/70 px-6 py-14 md:px-10 md:py-16 lg:px-14">
          <div className={`grid items-center gap-10 ${compositionImage ? 'lg:grid-cols-[0.8fr_1.2fr]' : ''}`}>
            {compositionImage ? (
              <div className="aspect-square overflow-hidden rounded-3xl border border-[#DECFAF] bg-white shadow-sm">
                <img src={compositionImage} alt={product.name} className="h-full w-full object-cover" />
              </div>
            ) : null}
            <div>
              <FoodSectionHeading
                eyebrow="Tiszta összetétel"
                title={details.compositionTitle || 'Ami nincs benne, az is számít'}
                description={details.compositionIntro}
              />
              {details.excludedIngredients.length > 0 ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {details.excludedIngredients.map((item) => (
                    <div key={item} className="flex items-center gap-3 rounded-full border border-[#DECFAF] bg-card px-4 py-3 shadow-sm">
                      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F5E6C8] text-[#A95610]">
                        <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                      </span>
                      <span className="text-sm text-foreground/70 line-through decoration-[#A95610]/60">{item}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {hasUsageSteps ? (
        <section className="bg-[#0F3D2C] px-6 py-14 text-white md:px-10 md:py-16 lg:px-14">
          <h2 className="font-display text-2xl font-semibold text-white md:text-3xl">
            {details.usageTitle || 'Használat lépésről lépésre'}
          </h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {details.usageSteps.map((step, index) => (
              <article key={`${step.title}-${index}`} className="rounded-2xl bg-white/[0.1] p-5">
                <p className="font-display text-2xl font-semibold text-[#DCCDBA]">{String(index + 1).padStart(2, '0')}</p>
                <h3 className="mt-3 font-display text-base font-semibold text-white">{step.title}</h3>
                {step.description ? <p className="mt-2 text-xs leading-5 text-white/75">{step.description}</p> : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

    </div>
  );
}

function HouseholdDetails({ product, onAddToCart }) {
  const details = normalizeHouseholdDetails(product.householdDetails);
  return (
    <>
      <HouseholdStoryDetails details={details} product={product} />
      <div className="grid gap-6 lg:grid-cols-[1.08fr_0.92fr]">
        <div className="space-y-6">
        {(product.usage || details.dosage) ? (
          <DetailPanel icon={Sparkles} title="Használat és adagolás">
            <dl>
              <LabelValue label="Használat" value={product.usage} />
              <LabelValue label="Adagolás / lépések" value={details.dosage} />
            </dl>
          </DetailPanel>
        ) : null}
        {details.suitableFor.length > 0 ? (
          <DetailPanel icon={Check} title="Mire használható?">
            <PillList items={details.suitableFor} />
          </DetailPanel>
        ) : null}
        </div>
        <div className="space-y-6">
        {product.ingredients?.length > 0 ? (
          <DetailPanel icon={Package} title="Összetétel">
            <p className="text-sm leading-relaxed text-foreground/75">{product.ingredients.join(', ')}</p>
          </DetailPanel>
        ) : null}
        {details.ecoFeatures.length > 0 ? (
          <DetailPanel icon={Leaf} title="Tudatos választás">
            <PillList items={details.ecoFeatures} />
          </DetailPanel>
        ) : null}
        {details.warnings ? (
          <DetailPanel icon={AlertTriangle} title="Fontos tudnivalók">
            <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/75">{details.warnings}</p>
          </DetailPanel>
        ) : null}
        </div>
      </div>
      <MarketingDetails details={details} product={product} onAddToCart={onAddToCart} />
    </>
  );
}

function BodycareDetails({ product, onAddToCart }) {
  const details = normalizeBodycareDetails(product.bodycareDetails);
  return (
    <>
      <div className="grid gap-6 lg:grid-cols-[1.08fr_0.92fr]">
        <div className="space-y-6">
        {details.application ? (
          <DetailPanel icon={Sparkles} title="Használat">
            <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/75">{details.application}</p>
          </DetailPanel>
        ) : null}
        {(details.suitableFor.length > 0 || details.skinHairTypes.length > 0) ? (
          <DetailPanel icon={Check} title="Neked ajánljuk, ha…">
            {details.suitableFor.length > 0 ? (
              <div>
                <p className="mb-3 text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">Felhasználás</p>
                <PillList items={details.suitableFor} />
              </div>
            ) : null}
            {details.skinHairTypes.length > 0 ? (
              <div className={details.suitableFor.length ? 'mt-5' : ''}>
                <p className="mb-3 text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">Bőr- / hajtípus</p>
                <PillList items={details.skinHairTypes} tone="warm" />
              </div>
            ) : null}
          </DetailPanel>
        ) : null}
        </div>
        <div className="space-y-6">
        {(details.activeIngredients.length > 0 || product.ingredients?.length > 0) ? (
          <DetailPanel icon={Droplets} title="Hatóanyagok és összetevők">
            <dl>
              <LabelValue label="Kiemelt hatóanyagok" value={details.activeIngredients.join(', ')} />
              <LabelValue label="Összetevők / INCI" value={(product.ingredients || []).join(', ')} />
            </dl>
          </DetailPanel>
        ) : null}
        {details.certifications.length > 0 ? (
          <DetailPanel icon={Leaf} title="Minősítések és jellemzők">
            <PillList items={details.certifications} />
          </DetailPanel>
        ) : null}
        {details.warnings ? (
          <DetailPanel icon={AlertTriangle} title="Fontos tudnivalók">
            <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/75">{details.warnings}</p>
          </DetailPanel>
        ) : null}
        </div>
      </div>
      <MarketingDetails details={details} product={product} onAddToCart={onAddToCart} />
    </>
  );
}

function GenericDetails({ product }) {
  return (
    <div className="grid gap-6 lg:grid-cols-[1.08fr_0.92fr]">
      <IntroPanel description={product.longDescription} />
      {product.ingredients?.length > 0 ? (
        <DetailPanel icon={Package} title="Összetevők">
          <p className="text-sm leading-relaxed text-foreground/75">{product.ingredients.join(', ')}</p>
        </DetailPanel>
      ) : null}
    </div>
  );
}

export function ProductDetailSections({ product, category, onAddToCart }) {
  const hasAnyDetail = product.longDescription
    || product.ingredients?.length
    || product.storage
    || product.usage;

  if (!hasAnyDetail && !product.foodDetails && !product.householdDetails && !product.bodycareDetails) {
    return null;
  }

  return (
    <div className="mt-16">
      {category !== 'foods' ? (
        <div className="mb-8 max-w-2xl">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary/70">
            Részletes termékinformáció
          </p>
          <h2 className="font-display text-2xl font-bold md:text-3xl">Minden, amit érdemes tudni</h2>
        </div>
      ) : null}
      {category === 'foods' ? <FoodDetails product={product} onAddToCart={onAddToCart} /> : null}
      {category === 'household' ? <HouseholdDetails product={product} onAddToCart={onAddToCart} /> : null}
      {category === 'bodycare' ? <BodycareDetails product={product} onAddToCart={onAddToCart} /> : null}
      {!['foods', 'household', 'bodycare'].includes(category) ? (
        <GenericDetails product={product} />
      ) : null}
    </div>
  );
}
